import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { router } from "./router";
import "./index.css";

// Swallow the benign "ResizeObserver loop completed with undelivered
// notifications" / "loop limit exceeded" warnings. They're a known no-op
// surfaced by libraries (ReactFlow, Radix popovers) that observe their
// own size — the browser bails out of the rAF and resumes on the next
// frame; nothing's actually broken. Three layers of defense, in order of
// effectiveness:
//   1. Wrap ResizeObserver so callbacks are deferred to rAF — this
//      eliminates the loop at the source, not just the overlay reporting.
//   2. Capture-phase window error listener — fires before any bubble-phase
//      overlay (e.g. Bun HMR) gets a chance to display.
//   3. console.error filter — some dev overlays intercept this channel
//      instead of (or in addition to) window error events.
// All three whitelist the exact message so real errors are unaffected.
const RESIZE_OBSERVER_RE =
  /ResizeObserver loop (?:limit exceeded|completed with undelivered notifications)/;

const NativeResizeObserver = window.ResizeObserver;
if (NativeResizeObserver) {
  window.ResizeObserver = class WrappedResizeObserver extends NativeResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

window.addEventListener(
  "error",
  (e) => {
    if (RESIZE_OBSERVER_RE.test(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  { capture: true },
);

const originalConsoleError = console.error;
console.error = (...args: unknown[]) => {
  const first = args[0];
  if (typeof first === "string" && RESIZE_OBSERVER_RE.test(first)) return;
  originalConsoleError(...args);
};

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);

if (import.meta.hot) {
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  createRoot(elem).render(app);
}
